import React from "react"
import type { StyleProps } from "@mallardbay/lib-react-components"
import {
    ActionBanner,
    ColorSchemes,
    ConditionalRenderContainer,
    HeadingLarge,
    HorizontalStack,
} from "@mallardbay/lib-react-components"

import type {
    QuoteFieldsFragment,
    QuoteListingFieldsFragment,
} from "~graphql/generated/graphql"
import ResponsiveBlock from "~components/shared/responsive-block"
import Box from "~components/shared/todo-lib-react-components/box"
import COPY from "~config/copy-constants"
import { useBookTrip } from "~components/pages/book/book-trip-provider"
import usePreCheckoutData from "~utils/use-pre-checkout-data"
import { useCheckoutFlow } from "~components/pages/listing/checkout/checkout-flow/checkout-flow-provider"

import BookingForm from "./booking-form"
import BookingSummary from "./booking-summary"
import BookingBreadcrumbs from "./booking-breadcrumbs"

interface Props {
    readonly listing: QuoteListingFieldsFragment
    readonly quote: QuoteFieldsFragment
    readonly isQuoteLoading: boolean
    readonly renderNavButtons?: () => React.ReactNode
}

export default function Book({
    listing,
    quote,
    isQuoteLoading,
    renderNavButtons,
}: Props) {
    const { isWidget } = useCheckoutFlow()

    const styles = useStyles({ isWidget })
    const { paymentMethodState } = useBookTrip()
    const { isAchAvailable } = usePreCheckoutData(listing.outfitter.id)

    const doesSportsmanPayProcessingFee =
        listing.outfitter.is_processing_fee_payed_by_sportsman
    const isPaymentMethodAch = paymentMethodState.isAchPaymentMethod
    const shouldShowActionBanner =
        doesSportsmanPayProcessingFee && !isPaymentMethodAch && isAchAvailable

    return (
        <ResponsiveBlock>
            <ConditionalRenderContainer shouldRender={!isWidget}>
                <HeadingLarge style={styles.title}>
                    {COPY.BOOK_YOUR_TRIP}
                </HeadingLarge>
            </ConditionalRenderContainer>

            <HorizontalStack style={styles.content}>
                <Box style={styles.left}>
                    <BookingBreadcrumbs listingId={listing.id} />
                    <BookingForm quote={quote} isWidget={isWidget} />
                </Box>
                <Box style={styles.right}>
                    <Box style={styles.sticky}>
                        <BookingSummary
                            quote={quote}
                            isQuoteLoading={isQuoteLoading}
                        />
                        {shouldShowActionBanner && (
                            <ActionBanner
                                title={COPY.SAVE_ON_FEES_WITH_ACH}
                                colorScheme={ColorSchemes.Brand}
                                style={styles.actionBanner}
                            >
                                {COPY.SAVE_ON_FEES_WITH_ACH_DESCRIPTION}
                            </ActionBanner>
                        )}
                        {renderNavButtons?.()}
                    </Box>
                </Box>
            </HorizontalStack>
        </ResponsiveBlock>
    )
}

function useStyles({ isWidget }: { isWidget?: boolean }) {
    const title: StyleProps = {
        marginTop: 5,
    }
    const content: StyleProps = {
        alignItems: "stretch",
        marginBottom: "40px",
    }
    const left: StyleProps = {
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        flex: 1,
        paddingTop: isWidget ? "0" : "30px",
    }
    const right: StyleProps = {
        display: { base: "none", md: "block" },
        width: "400px",
        minWidth: "400px",
        marginLeft: "30px",
    }
    const sticky: StyleProps = {
        top: "80px",
        position: "sticky",
    }

    const actionBanner: StyleProps = {
        marginTop: "20px",
    }

    return { title, content, left, right, sticky, actionBanner }
}
